import { useState, useEffect, useRef } from "react";
import { BRAND } from "../constants/brand";
import AIAssistantSection from "../components/AIAssistantSection";

/* ─── Data ─── */
const STATS = [
  { value: "200,000+", label: "Patients Tested Annually", icon: "🧪" },
  { value: "300+",     label: "Client Sites",              icon: "🏥" },
  { value: "100+",     label: "Team Members",              icon: "🔬" },
  { value: "400+",     label: "Lab Tests Available",       icon: "🗺️" },
];

const SERVICES = [
  {
    num: "01",
    title: "Blood & Urine Testing",
    desc: "Comprehensive blood and urine panels with customizable profiles. 24-hour turnaround on most tests with same-day results available.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <path d="M14 4C14 4 7 12 7 17a7 7 0 0014 0C21 12 14 4 14 4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
        <path d="M10 18a4 4 0 004-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    num: "02",
    title: "Toxicology",
    desc: "Drug screening with 100+ analytes, LC/MS confirmation, and medication compliance monitoring for healthcare and employer programs.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect x="9" y="3" width="10" height="5" rx="2" stroke="currentColor" strokeWidth="1.8"/>
        <path d="M9 8l-3 14h16L19 8" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
        <path d="M11 15h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        <path d="M12 19h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    num: "03",
    title: "Molecular / PCR Testing",
    desc: "Rapid pathogen identification — bacterial, viral, parasitic — using PCR technology with consistent 24-hour results.",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <circle cx="14" cy="14" r="10" stroke="currentColor" strokeWidth="1.8"/>
        <circle cx="14" cy="14" r="5" stroke="currentColor" strokeWidth="1.6"/>
        <circle cx="14" cy="14" r="2" fill="currentColor"/>
        <path d="M14 4v4M14 20v4M4 14h4M20 14h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      </svg>
    ),
  },
];

const PILLARS = [
  {
    title: "Quality Care",
    color: "#3b82f6",
    items: ["Dependable results every patient, every time", "400+ test varieties in our diagnostic menu", "Quick and reliable turnaround time", "Licensed under the most rigorous standards"],
    icon: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
        <path d="M13 3l2.5 7h7.5l-6 4.5 2.5 7L13 17l-6.5 4.5 2.5-7L3 10h7.5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    title: "Innovative Technology",
    color: "#8b5cf6",
    items: ["State-of-the-art equipment for diverse testing", "EMR integration for seamless workflows", "PCR testing for improved outcomes", "AI-powered diagnostic decision insights"],
    icon: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
        <rect x="3" y="6" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.8"/>
        <path d="M9 13l3 3 5-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    title: "Superior Service",
    color: "#10b981",
    items: ["Industry-accredited customer support staff", "Web portal access anytime, anywhere", "Clinical affairs team to help support patient care", "Courier and shipping services"],
    icon: (
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
        <circle cx="13" cy="10" r="4" stroke="currentColor" strokeWidth="1.8"/>
        <path d="M5 22c0-4.418 3.582-8 8-8s8 3.582 8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ),
  },
];

const NEWS = [
  { date: "May 15, 2026",       title: "What Does the Name Labarium Mean?", tag: "Company", page: "labarium-origin" },
  // { date: "September 23, 2025", title: "Q3 2025 Newsletter", tag: "Newsletter", href: "/Q3_newsletter.pdf" },
  // { date: "May 5, 2025",        title: "Q1 2025 Newsletter", tag: "Newsletter", href: "/Q1_newsletter_2025.pdf" },
];

const TICKER_ITEMS = [
  "1,500+ Specimens Processed Daily",
  "400+ Diagnostic Tests Available",
  "CAP Accredited",
  "20+ States Served",
  "24-Hour Result Turnaround",
  "AI-Powered Diagnostic Insights",
  "EMR Integration Ready",
  "35+ Insurance Plans Accepted",
];

/* ─── Ticker ─── */
function Ticker() {
  const doubled = [...TICKER_ITEMS, ...TICKER_ITEMS];
  return (
    <div style={{ background: BRAND.navyDeep, borderTop: `1px solid rgba(201,169,110,0.15)`, borderBottom: `1px solid rgba(201,169,110,0.15)`, overflow: "hidden", padding: "12px 0" }}>
      <div style={{ display: "flex", animation: "tickerScroll 28s linear infinite", width: "max-content" }}>
        {doubled.map((item, i) => (
          <span key={i} style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.55)", letterSpacing: "0.08em", whiteSpace: "nowrap", padding: "0 40px" }}>
            <span style={{ color: BRAND.gold, marginRight: 16 }}>◆</span>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─── Counter animation ─── */
function useCountUp(target, duration = 1800, start = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const num = parseInt(target.replace(/\D/g, ""), 10) || 0;
    if (!num) { setVal(target); return; }
    let frame;
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.round(eased * num));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [start, target, duration]);
  return typeof val === "number" ? val.toLocaleString() + (target.includes("+") ? "+" : "") : target;
}

function StatCard({ stat, delay }) {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStarted(true); }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const displayVal = useCountUp(stat.value, 1600, started);

  return (
    <div
      ref={ref}
      className="reveal"
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="stat-card-inner" style={{
        padding: "40px 32px",
        textAlign: "center",
        position: "relative",
        borderRight: delay < 300 ? `1px solid rgba(201,169,110,0.15)` : "none",
      }}>
        <div className="stat-number" style={{
          fontFamily: "'Playfair Display', serif",
          fontWeight: 800,
          fontSize: "clamp(36px, 4vw, 56px)",
          color: BRAND.navy,
          lineHeight: 1,
          letterSpacing: "-0.02em",
          background: `linear-gradient(135deg, ${BRAND.navy}, ${BRAND.navyLight})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}>
          {displayVal}
        </div>
        <div style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: "0.12em",
          color: BRAND.textLight,
          textTransform: "uppercase",
          marginTop: 10,
        }}>
          {stat.label}
        </div>
        <div style={{
          width: 32, height: 2,
          background: `linear-gradient(90deg, ${BRAND.gold}, transparent)`,
          margin: "12px auto 0",
        }} />
      </div>
    </div>
  );
}

/* ─── Main ─── */
export default function HomePage({ setPage }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 100); return () => clearTimeout(t); }, []);

  return (
    <>
      {/* ── HERO ── */}
      <section style={{
        background: `linear-gradient(150deg, ${BRAND.navyDeep} 0%, ${BRAND.navyDark} 45%, #001a45 100%)`,
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
        paddingTop: 76,
      }}>
        {/* Ambient glow blobs */}
        <div style={{
          position: "absolute", top: "15%", right: "8%",
          width: 480, height: 480, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(201,169,110,0.1) 0%, transparent 65%)",
          animation: "drift1 20s ease-in-out infinite",
          pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", bottom: "10%", left: "5%",
          width: 360, height: 360, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(10,74,158,0.4) 0%, transparent 65%)",
          animation: "drift2 26s ease-in-out infinite",
          pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", top: "60%", right: "25%",
          width: 200, height: 200, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(16,185,129,0.07) 0%, transparent 65%)",
          animation: "drift3 32s ease-in-out infinite",
          pointerEvents: "none",
        }} />

        {/* Grid */}
        <div style={{ position: "absolute", inset: 0, opacity: 0.025, pointerEvents: "none" }}>
          <svg width="100%" height="100%">
            <defs>
              <pattern id="hero-grid" width="56" height="56" patternUnits="userSpaceOnUse">
                <path d="M 56 0 L 0 0 0 56" fill="none" stroke="white" strokeWidth="0.6"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hero-grid)"/>
          </svg>
        </div>


        {/* Left accent */}
        <div style={{
          position: "absolute", left: 0, top: 0, width: 3, height: "100%",
          background: `linear-gradient(180deg, transparent, ${BRAND.gold}, transparent)`,
          animation: "glowPulse 4s ease-in-out infinite",
        }} />

        {/* ── Floating stat badges (right side) — aligned column ── */}
        <div className="hero-badges" style={{
          position: "absolute", top: "14%", right: "4%",
          display: "flex", flexDirection: "column", gap: 12,
          opacity: visible ? 1 : 0,
          transform: visible ? "none" : "translateY(24px)",
          transition: "all 1s cubic-bezier(.4,0,.2,1) 0.7s",
          zIndex: 2,
        }}>

          {/* Badge 1: Accreditations — first */}
          <div style={{
            background: "rgba(255,255,255,0.04)",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 14,
            padding: "14px 20px",
            boxShadow: "0 8px 28px rgba(0,0,0,0.22)",
            display: "flex", alignItems: "center", gap: 12,
          }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: BRAND.gold, flexShrink: 0 }} />
            <div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.75)", letterSpacing: "0.04em" }}>CAP · CLIA</div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.35)", letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 2 }}>Accredited Laboratory</div>
            </div>
          </div>

          {/* Badge 2: Tests available */}
          <div style={{
            background: "rgba(255,255,255,0.05)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(201,169,110,0.22)",
            borderRadius: 16,
            padding: "20px 26px",
            minWidth: 200,
            boxShadow: "0 16px 48px rgba(0,0,0,0.3)",
            position: "relative",
          }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${BRAND.gold}, transparent)`, borderRadius: "16px 16px 0 0" }} />
            <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 36, color: BRAND.gold, lineHeight: 1 }}>400+</div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.16em", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", marginTop: 6 }}>Lab Tests Available</div>
          </div>

          {/* Badge 3: Patients served */}
          <div style={{
            background: "rgba(255,255,255,0.04)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 16,
            padding: "20px 26px",
            minWidth: 200,
            boxShadow: "0 16px 48px rgba(0,0,0,0.25)",
            position: "relative",
          }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${BRAND.gold}, transparent)`, borderRadius: "16px 16px 0 0" }} />
            <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 36, color: BRAND.gold, lineHeight: 1 }}>200,000+</div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.16em", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", marginTop: 6 }}>Patients Tested Annually</div>
          </div>

        </div>

        <div className="rx-pad" style={{ maxWidth: 1400, margin: "0 auto", padding: "80px 40px", position: "relative", zIndex: 1, width: "100%" }}>
          <div style={{ maxWidth: 760 }}>

            {/* Eyebrow */}
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 12, marginBottom: 32,
              opacity: visible ? 1 : 0, transform: visible ? "none" : "translateY(20px)",
              transition: "all 0.7s cubic-bezier(.4,0,.2,1)",
            }}>
              <div style={{ width: 36, height: 1, background: BRAND.gold }} />
              <span style={{
                fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600,
                letterSpacing: "0.3em", color: BRAND.gold, textTransform: "uppercase",
              }}>
                The Healthcare Decision Company
              </span>
            </div>

            {/* Headline */}
            <h1 style={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 800,
              fontSize: "clamp(36px, 4.5vw, 64px)",
              lineHeight: 1.06,
              color: BRAND.white,
              margin: "0 0 28px",
              letterSpacing: "-0.01em",
              opacity: visible ? 1 : 0,
              transform: visible ? "none" : "translateY(32px)",
              transition: "all 0.85s cubic-bezier(.4,0,.2,1) 0.12s",
            }}>
              Improving Health Decisions to<br />
              <span style={{
                color: "transparent",
                background: `linear-gradient(135deg, ${BRAND.gold} 0%, ${BRAND.goldLight} 50%, ${BRAND.gold} 100%)`,
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                animation: "shimmer 4s linear infinite",
              }}>
                Save, Extend, and Improve Lives
              </span>
            </h1>

            {/* Sub */}
            <p style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 18,
              color: "rgba(255,255,255,0.62)",
              lineHeight: 1.75,
              marginBottom: 44,
              maxWidth: 580,
              fontWeight: 400,
              opacity: visible ? 1 : 0,
              transform: visible ? "none" : "translateY(20px)",
              transition: "all 0.85s cubic-bezier(.4,0,.2,1) 0.26s",
            }}>
              Laboratory test results inform 70% of all clinical decisions. Labarium processes biological and information specimens into actionable insights using AI and advanced laboratory testing — for clinicians, patients, insurers and health systems.
            </p>

            {/* CTAs */}
            <div className="hero-cta-row" style={{
              display: "flex", gap: 14, flexWrap: "wrap",
              opacity: visible ? 1 : 0,
              transform: visible ? "none" : "translateY(20px)",
              transition: "all 0.85s cubic-bezier(.4,0,.2,1) 0.4s",
            }}>
              <button
                onClick={() => setPage("about-us")}
                style={{
                  background: "linear-gradient(135deg, #c9a96e 0%, #dfc08a 50%, #c9a96e 100%)",
                  backgroundSize: "200% auto",
                  color: BRAND.navyDeep,
                  border: "none",
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 700,
                  fontSize: 13,
                  letterSpacing: "0.08em",
                  padding: "15px 32px",
                  borderRadius: 10,
                  cursor: "pointer",
                  boxShadow: "0 8px 32px rgba(201,169,110,0.4)",
                  textTransform: "uppercase",
                }}
                className="btn-shimmer"
              >
                Learn More
              </button>

              {[
                { label: "Join Our Lab Network", page: "lab-acquisitions" },
                { label: "Join Our Team", page: "careers" },
                { label: "Get Test Results", page: "find-patient-results" },
              ].map(btn => (
                <button
                  key={btn.page}
                  onClick={() => setPage(btn.page)}
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    backdropFilter: "blur(12px)",
                    color: BRAND.white,
                    border: "1.5px solid rgba(255,255,255,0.22)",
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 600,
                    fontSize: 13,
                    letterSpacing: "0.06em",
                    padding: "15px 28px",
                    borderRadius: 10,
                    cursor: "pointer",
                    transition: "all 0.25s ease",
                    textTransform: "uppercase",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = BRAND.gold; e.currentTarget.style.color = BRAND.gold; e.currentTarget.style.background = "rgba(201,169,110,0.1)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.22)"; e.currentTarget.style.color = BRAND.white; e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                >
                  {btn.label}
                </button>
              ))}
            </div>

            {/* Mission statement */}
            <div style={{
              marginTop: 56, paddingTop: 28,
              borderTop: "1px solid rgba(201,169,110,0.15)",
              opacity: visible ? 1 : 0,
              transition: "all 0.8s ease 0.6s",
              borderLeft: `3px solid ${BRAND.gold}`,
              paddingLeft: 20,
            }}>
              <p style={{
                fontFamily: "'Playfair Display', serif",
                fontStyle: "italic",
                fontSize: 15,
                color: "rgba(255,255,255,0.55)",
                lineHeight: 1.85,
                margin: 0,
                maxWidth: 640,
              }}>
                Health decisions are broken. Medical errors are the third leading cause of death. The top two leading causes of death — heart disease and cancer — can be prevented 90% of the time with better decisions. Beyond mortality, there is untold suffering in undiagnosed conditions, misdiagnosis, and treatment failures. <span style={{ color: BRAND.gold, fontWeight: 600 }}>With Labarium, we will do better.</span>
              </p>
            </div>
          </div>
        </div>

        {/* Hero bottom fade */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 120,
          background: "linear-gradient(transparent, rgba(0,14,36,0.4))",
          pointerEvents: "none",
        }} />
      </section>

      {/* ── AI ASSISTANT ── */}
      <AIAssistantSection />

      {/* ── TICKER ── */}
      <Ticker />

      {/* ── STATS ── */}
      <div style={{ background: BRAND.white, boxShadow: "0 4px 40px rgba(0,30,80,0.07)" }}>
        <div className="stat-grid rx-pad" style={{
          maxWidth: 1400, margin: "0 auto", padding: "0 40px",
          display: "grid", gridTemplateColumns: "repeat(4,1fr)",
        }}>
          {STATS.map((stat, i) => (
            <StatCard key={i} stat={stat} delay={i * 100} />
          ))}
        </div>
      </div>

      {/* ── SERVICES ── */}
      <section className="rx-pad" style={{
        background: `linear-gradient(160deg, ${BRAND.navyDeep} 0%, ${BRAND.navyDark} 100%)`,
        padding: "100px 40px",
        position: "relative", overflow: "hidden",
      }}>
        {/* Background decorations */}
        <div style={{ position: "absolute", top: -100, right: -100, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(201,169,110,0.06) 0%, transparent 65%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -80, left: -60, width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(10,74,158,0.25) 0%, transparent 65%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 1 }}>
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div className="reveal" style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
              <div style={{ width: 48, height: 1, background: BRAND.gold }} />
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.28em", color: BRAND.gold, textTransform: "uppercase" }}>Our Services</span>
              <div style={{ width: 48, height: 1, background: BRAND.gold }} />
            </div>
            <h2 className="reveal delay-100" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "clamp(28px,4vw,46px)", color: BRAND.white, margin: 0 }}>
              Comprehensive Laboratory Solutions
            </h2>
          </div>

          {/* Cards */}
          <div className="three-col" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24 }}>
            {SERVICES.map((s, i) => (
              <div
                key={i}
                className={`reveal delay-${(i + 1) * 100}`}
                style={{
                  background: "rgba(255,255,255,0.04)",
                  backdropFilter: "blur(16px)",
                  borderRadius: 20,
                  padding: "44px 40px",
                  border: "1px solid rgba(255,255,255,0.08)",
                  position: "relative", overflow: "hidden",
                  cursor: "default",
                  transition: "all 0.4s cubic-bezier(.4,0,.2,1)",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                  e.currentTarget.style.border = "1px solid rgba(201,169,110,0.3)";
                  e.currentTarget.style.transform = "translateY(-8px)";
                  e.currentTarget.style.boxShadow = "0 32px 80px rgba(0,0,0,0.4)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                  e.currentTarget.style.border = "1px solid rgba(255,255,255,0.08)";
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                {/* Top accent */}
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${BRAND.gold}, rgba(201,169,110,0.2))` }} />

                {/* Number */}
                <div style={{ position: "absolute", top: 20, right: 24, fontFamily: "'Playfair Display', serif", fontSize: 72, fontWeight: 700, color: "rgba(201,169,110,0.06)", lineHeight: 1 }}>{s.num}</div>

                {/* Icon */}
                <div style={{
                  width: 56, height: 56, borderRadius: 14,
                  background: "rgba(201,169,110,0.12)",
                  border: "1px solid rgba(201,169,110,0.2)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: BRAND.gold, marginBottom: 24,
                }}>
                  {s.icon}
                </div>

                <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 24, color: BRAND.gold, margin: "0 0 14px" }}>{s.title}</h3>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: "rgba(255,255,255,0.58)", lineHeight: 1.85, margin: 0, fontWeight: 400 }}>{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="reveal delay-400" style={{ textAlign: "center", marginTop: 52 }}>
            <button
              onClick={() => setPage("test-index")}
              style={{
                background: "transparent",
                color: BRAND.gold,
                border: `1.5px solid rgba(201,169,110,0.5)`,
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600, fontSize: 13,
                letterSpacing: "0.08em",
                padding: "14px 36px",
                borderRadius: 10,
                cursor: "pointer",
                textTransform: "uppercase",
                transition: "all 0.25s ease",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = BRAND.gold; e.currentTarget.style.color = BRAND.navyDeep; e.currentTarget.style.borderColor = BRAND.gold; e.currentTarget.style.boxShadow = "0 8px 28px rgba(201,169,110,0.35)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = BRAND.gold; e.currentTarget.style.borderColor = "rgba(201,169,110,0.5)"; e.currentTarget.style.boxShadow = "none"; }}
            >
              View Full Test Menu →
            </button>
          </div>
        </div>
      </section>

      {/* ── WHY LABARIUM ── */}
      <section className="rx-pad" style={{ background: BRAND.offWhite, padding: "108px 40px" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 72 }}>
            <div className="reveal" style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
              <div style={{ width: 48, height: 1, background: BRAND.gold }} />
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.28em", color: BRAND.gold, textTransform: "uppercase" }}>Our Commitment</span>
              <div style={{ width: 48, height: 1, background: BRAND.gold }} />
            </div>
            <h2 className="reveal delay-100" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "clamp(28px,4vw,46px)", color: BRAND.navy, margin: 0 }}>
              Why Choose Labarium
            </h2>
            <p className="reveal delay-200" style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: BRAND.textLight, marginTop: 16, maxWidth: 520, marginLeft: "auto", marginRight: "auto", lineHeight: 1.7, fontWeight: 400 }}>
              Three pillars that set us apart in diagnostic laboratory services.
            </p>
          </div>

          <div className="three-col" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 28 }}>
            {PILLARS.map((pillar, i) => (
              <div
                key={i}
                className={`reveal delay-${(i + 1) * 150}`}
                style={{
                  background: BRAND.white,
                  borderRadius: 20,
                  padding: "52px 44px",
                  boxShadow: "0 4px 24px rgba(0,30,80,0.06)",
                  border: `1px solid ${BRAND.lightGray}`,
                  position: "relative", overflow: "hidden",
                  transition: "all 0.4s cubic-bezier(.4,0,.2,1)",
                  cursor: "default",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = "translateY(-10px)";
                  e.currentTarget.style.boxShadow = "0 28px 72px rgba(0,30,80,0.15)";
                  e.currentTarget.style.borderColor = "rgba(201,169,110,0.3)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "0 4px 24px rgba(0,30,80,0.06)";
                  e.currentTarget.style.borderColor = BRAND.lightGray;
                }}
              >
                {/* Top color bar */}
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${BRAND.navy}, ${BRAND.navyLight})` }} />

                {/* Large background number */}
                <div style={{ position: "absolute", top: 16, right: 24, fontFamily: "'Playfair Display', serif", fontSize: 88, fontWeight: 700, color: "rgba(0,53,122,0.04)", lineHeight: 1 }}>0{i+1}</div>

                {/* Icon */}
                <div style={{
                  width: 56, height: 56, borderRadius: 14,
                  background: `linear-gradient(135deg, ${BRAND.navy}, ${BRAND.navyLight})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: BRAND.white, marginBottom: 28,
                  boxShadow: "0 8px 24px rgba(0,53,122,0.3)",
                }}>
                  {pillar.icon}
                </div>

                <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 26, color: BRAND.navy, margin: "0 0 24px" }}>{pillar.title}</h3>

                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {pillar.items.map((item, j) => (
                    <li key={j} style={{ display: "flex", alignItems: "flex-start", gap: 12, fontFamily: "'Inter', sans-serif", fontSize: 14, color: BRAND.textMid, lineHeight: 1.65, marginBottom: 13, fontWeight: 400 }}>
                      <span style={{
                        width: 18, height: 18, borderRadius: "50%",
                        background: "rgba(201,169,110,0.12)",
                        border: "1px solid rgba(201,169,110,0.3)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, marginTop: 2,
                      }}>
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 4l2 2 4-4" stroke="#c9a96e" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CERTIFICATIONS STRIP ── */}
      <div className="rx-pad" style={{
        background: BRAND.white,
        borderTop: `1px solid ${BRAND.lightGray}`,
        borderBottom: `1px solid ${BRAND.lightGray}`,
        padding: "32px 40px",
      }}>
        <div style={{
          maxWidth: 1400, margin: "0 auto",
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 40, flexWrap: "wrap",
        }}>
          <span className="reveal" style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.2em", color: BRAND.textLight, textTransform: "uppercase" }}>Accredited &amp; Certified:</span>
          {["CAP Accredited", "CLIA Certified", "OIG Compliance", "Medicare Approved"].map((cert, i) => (
            <div key={i} className={`reveal delay-${(i + 1) * 80}`} style={{
              display: "flex", alignItems: "center", gap: 8,
              fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600,
              color: BRAND.navy, letterSpacing: "0.04em",
            }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: BRAND.gold }} />
              {cert}
            </div>
          ))}
        </div>
      </div>

      {/* ── HOURS + CTA ── */}
      <section className="rx-pad" style={{
        background: `linear-gradient(150deg, ${BRAND.navyDeep} 0%, ${BRAND.navyDark} 100%)`,
        padding: "88px 40px",
        position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: -60, right: -60, width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(201,169,110,0.07) 0%, transparent 65%)", pointerEvents: "none" }} />

        <div className="hours-grid" style={{ maxWidth: 1400, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1px 1fr", gap: 64, alignItems: "center", position: "relative", zIndex: 1 }}>

          {/* Hours */}
          <div className="reveal-left">
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.25em", color: BRAND.gold, marginBottom: 32, textTransform: "uppercase" }}>
              Operating Hours
            </div>

            <div style={{ marginBottom: 28 }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.18em", color: BRAND.gold, marginBottom: 10, opacity: 0.7, textTransform: "uppercase" }}>Customer Service</div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: BRAND.white, fontWeight: 700 }}>Monday – Friday</div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 17, color: "rgba(255,255,255,0.6)", marginTop: 4, fontWeight: 400 }}>8:00 AM – 5:30 PM</div>
            </div>

            {/* Draw Station hours hidden
            <div style={{ width: "100%", height: 1, background: "rgba(201,169,110,0.2)", marginBottom: 28 }} />
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.18em", color: BRAND.gold, marginBottom: 10, opacity: 0.7, textTransform: "uppercase" }}>Draw Station</div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: BRAND.white, fontWeight: 700 }}>Monday – Friday</div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 17, color: "rgba(255,255,255,0.6)", marginTop: 4, fontWeight: 400 }}>8:30 AM – 3:45 PM</div>
            </div>
            */}

            <div style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 12, padding: "16px 20px",
              marginTop: 28,
            }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.55)", lineHeight: 1.9, fontWeight: 400 }}>
                <div style={{ fontWeight: 600, color: "rgba(255,255,255,0.7)", marginBottom: 2 }}>Phone</div>
                <div>(850) 460-1484</div>
                <div style={{ marginTop: 6, fontWeight: 600, color: "rgba(255,255,255,0.7)", marginBottom: 2 }}>Toll-Free</div>
                <div>(877) 765-2274</div>
                <div style={{ marginTop: 10, fontWeight: 600, color: "rgba(255,255,255,0.7)", marginBottom: 2 }}>Florida (HQ)</div>
                <div>495 Grand Blvd #206, Miramar Beach, FL 32550</div>
                <div style={{ marginTop: 10, fontWeight: 600, color: "rgba(255,255,255,0.7)", marginBottom: 2 }}>Florida Laboratory, Infiniti Labs</div>
                <div>6015 Benjamin Rd #315-A, Tampa, FL 33634</div>
                <div style={{ marginTop: 10, fontWeight: 600, color: "rgba(255,255,255,0.7)", marginBottom: 2 }}>Labarium Affiliated Laboratory, Med-Lake Laboratory</div>
                <div>(478) 414-4235 · Fax: (478) 414-4247</div>
                <div>100 Industrial Park Drive, Milledgeville, GA 31061</div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="hours-divider" style={{ background: "rgba(201,169,110,0.2)", height: "80%", alignSelf: "center" }} />

          {/* CTA */}
          <div className="reveal-right" style={{ textAlign: "center" }}>
            {/* Decorative circle */}
            <div style={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(201,169,110,0.1)", border: "1px solid rgba(201,169,110,0.25)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 28px" }}>
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="12" stroke={BRAND.gold} strokeWidth="1.5"/>
                <path d="M16 10v6l4 3" stroke={BRAND.gold} strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </div>

            <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "clamp(24px,3vw,38px)", color: BRAND.white, marginBottom: 12, lineHeight: 1.2 }}>
              Ready to Get Started?
            </div>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: "rgba(255,255,255,0.55)", marginBottom: 36, lineHeight: 1.7, fontWeight: 400 }}>
              Contact our support team or visit one of our laboratory locations.
            </p>
            <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
              <button
                onClick={() => setPage("make-an-appointment")}
                style={{
                  background: "linear-gradient(135deg, #c9a96e 0%, #dfc08a 50%, #c9a96e 100%)",
                  backgroundSize: "200% auto",
                  color: BRAND.navyDeep,
                  border: "none",
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 700, fontSize: 12,
                  letterSpacing: "0.08em",
                  padding: "14px 30px",
                  borderRadius: 10, cursor: "pointer",
                  boxShadow: "0 6px 24px rgba(201,169,110,0.35)",
                  textTransform: "uppercase",
                }}
                className="btn-shimmer"
              >
                Make an Appointment
              </button>
              <button
                onClick={() => setPage("contact-us")}
                style={{
                  background: "transparent",
                  color: BRAND.white,
                  border: "1.5px solid rgba(255,255,255,0.2)",
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600, fontSize: 12,
                  letterSpacing: "0.06em",
                  padding: "14px 30px",
                  borderRadius: 10, cursor: "pointer",
                  transition: "all 0.25s ease",
                  textTransform: "uppercase",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = BRAND.gold; e.currentTarget.style.color = BRAND.gold; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; e.currentTarget.style.color = BRAND.white; }}
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── NEWS ── */}
      <section className="rx-pad" style={{ background: BRAND.white, padding: "108px 40px" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 56, flexWrap: "wrap", gap: 20 }}>
            <div>
              <div className="reveal" style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
                <div style={{ width: 48, height: 1, background: BRAND.gold }} />
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.28em", color: BRAND.gold, textTransform: "uppercase" }}>Latest News</span>
              </div>
              <h2 className="reveal delay-100" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "clamp(28px,4vw,46px)", color: BRAND.navy, margin: 0 }}>
                News &amp; Updates
              </h2>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={{ width: "100%", maxWidth: NEWS.length === 1 ? 480 : "100%", display: "grid", gridTemplateColumns: `repeat(${Math.min(NEWS.length, 3)},1fr)`, gap: 28 }}>
            {NEWS.map((item, i) => {
              const sharedStyle = {
                border: `1px solid ${BRAND.lightGray}`, borderRadius: 20,
                overflow: "hidden", cursor: "pointer",
                transition: "all 0.4s cubic-bezier(.4,0,.2,1)",
                background: BRAND.white, textDecoration: "none", display: "block",
              };
              const onEnter = e => { e.currentTarget.style.transform = "translateY(-8px)"; e.currentTarget.style.boxShadow = "0 24px 64px rgba(0,30,80,0.14)"; e.currentTarget.style.borderColor = "rgba(201,169,110,0.3)"; };
              const onLeave = e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = BRAND.lightGray; };
              const cardInner = (
                <>
                  <div style={{ background: `linear-gradient(150deg, ${BRAND.navyDeep}, ${BRAND.navyDark})`, padding: "32px 32px 28px", position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${BRAND.gold}, rgba(201,169,110,0.2))` }} />
                    <div style={{ position: "absolute", bottom: -20, right: -20, width: 120, height: 120, borderRadius: "50%", background: "rgba(201,169,110,0.04)" }} />
                    <span style={{ display: "inline-block", background: "rgba(201,169,110,0.15)", color: BRAND.gold, fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.2em", padding: "4px 12px", borderRadius: 6, marginBottom: 14, textTransform: "uppercase", border: "1px solid rgba(201,169,110,0.25)" }}>
                      {item.tag}
                    </span>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: "rgba(255,255,255,0.4)", fontWeight: 400 }}>{item.date}</div>
                  </div>
                  <div style={{ padding: "28px 32px 36px" }}>
                    <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: 18, color: BRAND.navy, lineHeight: 1.5, margin: "0 0 20px" }}>{item.title}</h3>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 600, color: BRAND.gold, letterSpacing: "0.08em" }}>Read More</span>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 3l4 4-4 4" stroke={BRAND.gold} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                  </div>
                </>
              );
              return item.page
                ? <div key={i} className={`reveal delay-${(i + 1) * 120}`} style={sharedStyle} onMouseEnter={onEnter} onMouseLeave={onLeave} onClick={() => setPage(item.page)}>{cardInner}</div>
                : <a key={i} href={item.href} target="_blank" rel="noreferrer" className={`reveal delay-${(i + 1) * 120}`} style={sharedStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>{cardInner}</a>;
            })}
          </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA BANNER ── */}
      <section className="rx-pad" style={{
        background: `linear-gradient(135deg, ${BRAND.navyDeep} 0%, ${BRAND.navy} 100%)`,
        padding: "80px 40px",
        position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", inset: 0, opacity: 0.025, pointerEvents: "none" }}>
          <svg width="100%" height="100%"><defs><pattern id="fin-grid" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5"/></pattern></defs><rect width="100%" height="100%" fill="url(#fin-grid)"/></svg>
        </div>
        <div style={{ maxWidth: 1400, margin: "0 auto", textAlign: "center", position: "relative", zIndex: 1 }}>
          <div className="reveal" style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.3em", color: BRAND.gold, marginBottom: 20, textTransform: "uppercase" }}>
            Better information. Better decisions. Better lives.
          </div>
          <h2 className="reveal delay-100" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "clamp(28px,4vw,52px)", color: BRAND.white, margin: "0 0 16px", lineHeight: 1.15 }}>
            Trusted by Clinicians Across 20+ States
          </h2>
          <p className="reveal delay-200" style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: "rgba(255,255,255,0.55)", maxWidth: 520, margin: "0 auto 40px", lineHeight: 1.7, fontWeight: 400 }}>
            Join the growing network of healthcare providers who rely on Labarium for fast, accurate, and actionable diagnostic insights.
          </p>
          <div className="reveal delay-300" style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => setPage("lab-acquisitions")}
              style={{
                background: "linear-gradient(135deg, #c9a96e 0%, #dfc08a 50%, #c9a96e 100%)",
                backgroundSize: "200% auto",
                color: BRAND.navyDeep,
                border: "none",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700, fontSize: 13,
                letterSpacing: "0.08em",
                padding: "16px 44px",
                borderRadius: 10, cursor: "pointer",
                boxShadow: "0 8px 32px rgba(201,169,110,0.4)",
                textTransform: "uppercase",
              }}
              className="btn-shimmer"
            >
              Join Our Lab Network
            </button>
            <button
              onClick={() => setPage("about-us")}
              style={{
                background: "rgba(255,255,255,0.07)",
                color: BRAND.white,
                border: "1.5px solid rgba(255,255,255,0.18)",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600, fontSize: 13,
                letterSpacing: "0.06em",
                padding: "16px 44px",
                borderRadius: 10, cursor: "pointer",
                transition: "all 0.25s ease",
                textTransform: "uppercase",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = BRAND.gold; e.currentTarget.style.color = BRAND.gold; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)"; e.currentTarget.style.color = BRAND.white; }}
            >
              Learn About Us
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
